import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import DataUploadSection from './components/DataUploadSection';
import DatabaseConnections from './components/DatabaseConnections';
import DataMappingTools from './components/DataMappingTools';
import ImportHistory from './components/ImportHistory';

export default function MaterialDatabaseIntegrationHub() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('connections');

  const sections = [
    { id: 'upload', label: 'Upload Data', icon: 'Upload', description: 'Import compatibility data files' },
    { id: 'connections', label: 'Database Links', icon: 'Database', description: 'Connect to external databases' },
    { id: 'mapping', label: 'Data Mapping', icon: 'GitBranch', description: 'Map data fields and formats' },
    { id: 'history', label: 'Import History', icon: 'Clock', description: 'View previous imports' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Material Database Integration Hub</h1>
              <p className="text-sm text-gray-600 mt-1">Connect, sync, and manage external material compatibility databases</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Icon name="Home" size={16} />
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1">
        <div className="p-4 lg:p-8">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="Database" size={20} className="text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">5</div>
                  <div className="text-sm text-muted-foreground">Active Connections</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <Icon name="Upload" size={20} className="text-green-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">1,247</div>
                  <div className="text-sm text-muted-foreground">Records Imported</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Icon name="CheckCircle" size={20} className="text-blue-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">98%</div>
                  <div className="text-sm text-muted-foreground">Data Quality</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/10 rounded-lg">
                  <Icon name="Clock" size={20} className="text-yellow-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">12</div>
                  <div className="text-sm text-muted-foreground">Pending Reviews</div>
                </div>
              </div>
            </div>
          </div>

          {/* Section Navigation */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {sections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => setActiveTab(section?.id)}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  activeTab === section?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    activeTab === section?.id ? 'bg-primary/10' : 'bg-accent'
                  }`}>
                    <Icon
                      name={section?.icon}
                      size={20}
                      className={activeTab === section?.id ? 'text-primary' : 'text-muted-foreground'}
                    />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">{section?.label}</div>
                    <div className="text-xs text-muted-foreground">{section?.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Section Content */}
          <div>
            {activeTab === 'upload' && <DataUploadSection />}
            {activeTab === 'connections' && <DatabaseConnections />}
            {activeTab === 'mapping' && <DataMappingTools />}
            {activeTab === 'history' && <ImportHistory />}
          </div>
        </div>
      </main>
    </div>
  );
}